import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { MessagingContact } from "@/contracts";
import {
  ALERT_METRICS,
  CONNECTIVITY_DURATION_MIN_SECONDS,
  DEFAULT_ALERT_RULES,
  MAX_RECIPIENTS,
  VOICE_WAIT_DEFAULT_MINUTES,
  VOICE_WAIT_MAX_MINUTES,
  VOICE_WAIT_MIN_MINUTES,
  RULE_BOUNDED_METRICS,
  RULE_DURATION_MIN_SECONDS,
  RULE_TIMER_MAX_SECONDS,
  SEVERITIES,
  type AlertMetric,
  type AlertRule,
  type CreateAlertRuleRequest,
  type Severity,
  type UpdateAlertRuleRequest,
} from "@/contracts";

import {
  createAlertRuleMutation,
  messagingContactsQuery,
  organizationOptionsQuery,
  ruleDeviceOptionsQuery,
  updateAlertRuleMutation,
} from "@/api/queries";
import { useAuth } from "@/auth/auth-context";
import { Alert } from "@/components/ui/alert";
import { TextAreaField, TextField, SelectField } from "@/components/ui/field";
import { FormDialog } from "@/components/ui/form-dialog";
import { SEVERITY } from "@/features/devices/device-labels";
import { useApiClient } from "@/lib/api-context";
import {
  ALERT_METRIC_LABELS,
  ALERT_METRIC_UNITS,
  formatBounds,
  metricHasRuleTimers,
} from "./alert-labels";

/**
 * Metrics whose rules carry numeric min/max. The rest are bit/schedule/silence
 * rules — bounds are not meaningful, and the API refuses them on create.
 */
function metricHasNumericBounds(metric: AlertMetric): boolean {
  return (RULE_BOUNDED_METRICS as readonly AlertMetric[]).includes(metric);
}

const TIMER_MAX_MINUTES = RULE_TIMER_MAX_SECONDS / 60;

function durationMinMinutes(metric: AlertMetric): number {
  return (
    (metric === "CONNECTIVITY" ? CONNECTIVITY_DURATION_MIN_SECONDS : RULE_DURATION_MIN_SECONDS) / 60
  );
}

function parseOptionalNumber(raw: string): number | null | undefined {
  const trimmed = raw.trim();
  if (trimmed === "") {
    return null;
  }

  const value = Number(trimmed);
  return Number.isFinite(value) ? value : undefined;
}

/**
 * Splits on anything a person might reasonably type between addresses.
 *
 * Pasting from a mail client gives commas and semicolons; typing a list gives
 * newlines. Accepting all three is cheaper than telling somebody their paste
 * was the wrong shape.
 */
function parseRecipients(input: string): string[] {
  return input
    .split(/[\s,;]+/)
    .map((value) => value.trim())
    .filter((value) => value.length > 0);
}

/** Matches the API's rule, so the error appears before the round trip. */
function invalidAddresses(addresses: readonly string[]): string[] {
  return addresses.filter((address) => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(address));
}

function boundText(value: number | null): string {
  return value === null ? "" : String(value);
}

function minutesText(seconds: number): string {
  return String(Math.round(seconds / 60));
}

/** The editable part of a rule, as the form holds it. */
interface RuleValues {
  minValue: number | null;
  maxValue: number | null;
  durationSeconds: number;
  cooldownSeconds: number;
  severity: Severity;
  enabled: boolean;
  recipients: readonly string[];
  whatsappContactIds: readonly string[];
  voiceContactIds: readonly string[];
  voiceWaitMinutes: number | null;
}

interface TimedValues {
  recipients: string[];
  enabled: boolean;
  severity: Severity;
  durationSeconds?: number;
  cooldownSeconds?: number;
  minValue?: number | null;
  maxValue?: number | null;
  notifyEmail: boolean;
  notifyWhatsapp: boolean;
  notifyVoice: boolean;
  whatsappContactIds: string[];
  voiceContactIds: string[];
  voiceWaitMinutes: number | null;
}

/**
 * The fields create and edit share: bounds, timers, severity, enabled flag
 * and who hears. `metric` decides which of them are asked at all.
 */
function useRuleFields(metric: AlertMetric, initial: RuleValues) {
  const [minText, setMinText] = useState(boundText(initial.minValue));
  const [maxText, setMaxText] = useState(boundText(initial.maxValue));
  const [durationMinutes, setDurationMinutes] = useState(minutesText(initial.durationSeconds));
  const [cooldownMinutes, setCooldownMinutes] = useState(minutesText(initial.cooldownSeconds));
  const [severity, setSeverity] = useState<Severity>(initial.severity);
  const [enabled, setEnabled] = useState(initial.enabled);
  const [recipientsText, setRecipientsText] = useState(initial.recipients.join("\n"));
  const [emailOn, setEmailOn] = useState(initial.recipients.length > 0);
  const [whatsappOn, setWhatsappOn] = useState(initial.whatsappContactIds.length > 0);
  const [whatsappIds, setWhatsappIds] = useState<string[]>([...initial.whatsappContactIds]);
  const [voiceOn, setVoiceOn] = useState(initial.voiceContactIds.length > 0);
  const [voiceIds, setVoiceIds] = useState<string[]>([...initial.voiceContactIds]);
  const [voiceWait, setVoiceWait] = useState(
    String(initial.voiceWaitMinutes ?? VOICE_WAIT_DEFAULT_MINUTES),
  );

  const recipients = emailOn ? parseRecipients(recipientsText) : [];
  const invalid = invalidAddresses(recipients);
  const tooMany = recipients.length > MAX_RECIPIENTS;

  /** A metric picked in the create dialog starts from its own defaults. */
  function reset(
    values: Omit<
      RuleValues,
      "enabled" | "recipients" | "whatsappContactIds" | "voiceContactIds" | "voiceWaitMinutes"
    >,
  ) {
    setMinText(boundText(values.minValue));
    setMaxText(boundText(values.maxValue));
    setDurationMinutes(minutesText(values.durationSeconds));
    setCooldownMinutes(minutesText(values.cooldownSeconds));
    setSeverity(values.severity);
  }

  /** The request body, or an error key to show instead of sending it. */
  function collect():
    { body: TimedValues } | { error: string; params: Record<string, number> } | null {
    const waitRaw = Number(voiceWait.trim());
    const waitInvalid =
      voiceOn &&
      (voiceWait.trim() === "" ||
        !Number.isInteger(waitRaw) ||
        waitRaw < VOICE_WAIT_MIN_MINUTES ||
        waitRaw > VOICE_WAIT_MAX_MINUTES);

    if ((emailOn && (invalid.length > 0 || tooMany || recipients.length === 0)) || waitInvalid) {
      return null;
    }

    if (whatsappOn && whatsappIds.length === 0) {
      return null;
    }

    if (voiceOn && voiceIds.length === 0) {
      return null;
    }

    const body: TimedValues = {
      recipients: emailOn ? recipients : [],
      enabled,
      severity,
      notifyEmail: emailOn,
      notifyWhatsapp: whatsappOn,
      notifyVoice: voiceOn,
      whatsappContactIds: whatsappOn ? whatsappIds : [],
      voiceContactIds: voiceOn ? voiceIds : [],
      voiceWaitMinutes: voiceOn ? waitRaw : null,
    };

    if (metricHasRuleTimers(metric)) {
      const minDuration = durationMinMinutes(metric);
      const durationRaw = Number(durationMinutes.trim());
      const cooldownRaw = Number(cooldownMinutes.trim());

      if (
        durationMinutes.trim() === "" ||
        !Number.isInteger(durationRaw) ||
        durationRaw < minDuration ||
        durationRaw > TIMER_MAX_MINUTES
      ) {
        return {
          error: "editRuleDurationInvalid",
          params: { min: minDuration, max: TIMER_MAX_MINUTES },
        };
      }

      if (
        cooldownMinutes.trim() === "" ||
        !Number.isInteger(cooldownRaw) ||
        cooldownRaw < 0 ||
        cooldownRaw > TIMER_MAX_MINUTES
      ) {
        return { error: "editRuleCooldownInvalid", params: { max: TIMER_MAX_MINUTES } };
      }

      body.durationSeconds = durationRaw * 60;
      body.cooldownSeconds = cooldownRaw * 60;
    }

    if (metricHasNumericBounds(metric)) {
      const minValue = parseOptionalNumber(minText);
      const maxValue = parseOptionalNumber(maxText);

      if (minValue === undefined || maxValue === undefined) {
        return { error: "editRuleBoundsInvalid", params: {} };
      }

      if (minValue !== null && maxValue !== null && minValue >= maxValue) {
        return { error: "editRuleBoundsOrder", params: {} };
      }

      body.minValue = minValue;
      body.maxValue = maxValue;
    }

    return { body };
  }

  return {
    state: {
      minText,
      maxText,
      durationMinutes,
      cooldownMinutes,
      severity,
      enabled,
      recipientsText,
      recipients,
      invalid,
      tooMany,
      emailOn,
      whatsappOn,
      whatsappIds,
      voiceOn,
      voiceIds,
      voiceWait,
      waitInvalid:
        voiceOn &&
        (voiceWait.trim() === "" ||
          !Number.isInteger(Number(voiceWait.trim())) ||
          Number(voiceWait.trim()) < VOICE_WAIT_MIN_MINUTES ||
          Number(voiceWait.trim()) > VOICE_WAIT_MAX_MINUTES),
    },
    set: {
      setMinText,
      setMaxText,
      setDurationMinutes,
      setCooldownMinutes,
      setSeverity,
      setEnabled,
      setRecipientsText,
      setEmailOn: (on: boolean) => {
        setEmailOn(on);

        if (!on) {
          setRecipientsText("");
        }
      },
      setWhatsappOn: (on: boolean) => {
        setWhatsappOn(on);

        if (!on) {
          setWhatsappIds([]);
        }
      },
      toggleWhatsapp: (id: string) => {
        setWhatsappIds((current) =>
          current.includes(id) ? current.filter((value) => value !== id) : [...current, id],
        );
      },
      setVoiceOn: (on: boolean) => {
        setVoiceOn(on);

        if (!on) {
          setVoiceIds([]);
          setVoiceWait(String(VOICE_WAIT_DEFAULT_MINUTES));
        }
      },
      toggleVoice: (id: string) => {
        setVoiceIds((current) =>
          current.includes(id) ? current.filter((value) => value !== id) : [...current, id],
        );
      },
      setVoiceWait,
    },
    reset,
    collect,
  };
}

type RuleFields = ReturnType<typeof useRuleFields>;

function ChannelToggle({
  id,
  label,
  hint,
  checked,
  onChange,
}: {
  id: string;
  label: string;
  hint: string;
  checked: boolean;
  onChange: (on: boolean) => void;
}) {
  return (
    <div className="flex items-start gap-2">
      <input
        id={id}
        type="checkbox"
        className="mt-1 size-4 rounded border-border-subtle"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
      />
      <label htmlFor={id} className="text-sm text-ink">
        <span className="font-medium">{label}</span>
        <span className="mt-0.5 block text-xs text-ink-muted">{hint}</span>
      </label>
    </div>
  );
}

function ContactChoices({
  legend,
  empty,
  contacts,
  selected,
  onToggle,
  pending,
  error,
}: {
  legend: string;
  empty: string;
  contacts: readonly MessagingContact[];
  selected: readonly string[];
  onToggle: (id: string) => void;
  pending: boolean;
  error?: string;
}) {
  return (
    <fieldset className="flex flex-col gap-2">
      <legend className="text-sm font-medium text-ink">{legend}</legend>
      {!pending && contacts.length === 0 ? <p className="text-sm text-ink-muted">{empty}</p> : null}
      {contacts.map((contact) => (
        <label key={contact.id} className="flex items-start gap-2 text-sm text-ink">
          <input
            type="checkbox"
            className="mt-1 size-4 rounded border-border-subtle"
            checked={selected.includes(contact.id)}
            onChange={() => onToggle(contact.id)}
          />
          <span>
            {contact.displayName}
            <span className="block text-xs text-ink-muted tabular-nums">{contact.phone}</span>
          </span>
        </label>
      ))}
      {error ? <p className="text-sm text-danger">{error}</p> : null}
    </fieldset>
  );
}

function RuleFieldset({
  metric,
  fields,
  storedBounds,
  organizationId,
}: {
  metric: AlertMetric;
  fields: RuleFields;
  storedBounds: { minValue: number | null; maxValue: number | null };
  organizationId?: string;
}) {
  const { t } = useTranslation("pages");
  const client = useApiClient();
  const enabledId = useId();
  const emailId = useId();
  const whatsappId = useId();
  const voiceId = useId();
  const wantsContacts = fields.state.whatsappOn || fields.state.voiceOn;
  const contacts = useQuery({
    ...messagingContactsQuery(client, {
      ...(organizationId ? { organizationId } : {}),
      limit: 100,
    }),
    enabled: wantsContacts && (organizationId === undefined || organizationId !== ""),
  });
  const { state, set } = fields;
  const unit = ALERT_METRIC_UNITS[metric];
  const rows = contacts.data?.data ?? [];
  const whatsappChoices = rows.filter(
    (contact) => contact.consentMethod !== null && contact.revokedAt === null,
  );
  const voiceChoices = rows.filter(
    (contact) => contact.voiceConsentMethod !== null && contact.voiceRevokedAt === null,
  );
  const hears =
    (state.emailOn && state.recipients.length > 0 && state.invalid.length === 0 && !state.tooMany) ||
    (state.whatsappOn && state.whatsappIds.length > 0) ||
    (state.voiceOn && state.voiceIds.length > 0);
  const minDuration = durationMinMinutes(metric);

  return (
    <>
      {metricHasNumericBounds(metric) ? (
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            label={t("fieldRuleMin")}
            hint={t("fieldRuleBoundHint", { unit: unit || t("fieldRuleNoUnit") })}
            type="number"
            step="any"
            value={state.minText}
            onChange={(event) => set.setMinText(event.target.value)}
          />
          <TextField
            label={t("fieldRuleMax")}
            hint={t("fieldRuleBoundHint", { unit: unit || t("fieldRuleNoUnit") })}
            type="number"
            step="any"
            value={state.maxText}
            onChange={(event) => set.setMaxText(event.target.value)}
          />
        </div>
      ) : (
        <Alert tone="info" title={t("editRuleNoBoundsTitle")} announce={false}>
          {formatBounds(metric, storedBounds.minValue, storedBounds.maxValue)}
        </Alert>
      )}

      {metricHasRuleTimers(metric) ? (
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            label={t("fieldDurationMinutes")}
            hint={t(
              metric === "CONNECTIVITY"
                ? "fieldDurationConnectivityHint"
                : "fieldDurationMinutesHint",
              { min: minDuration, max: TIMER_MAX_MINUTES },
            )}
            type="number"
            min={minDuration}
            max={TIMER_MAX_MINUTES}
            step={1}
            required
            value={state.durationMinutes}
            onChange={(event) => set.setDurationMinutes(event.target.value)}
          />
          <TextField
            label={t("fieldCooldownMinutes")}
            hint={t("fieldCooldownMinutesHint", { max: TIMER_MAX_MINUTES })}
            type="number"
            min={0}
            max={TIMER_MAX_MINUTES}
            step={1}
            required
            value={state.cooldownMinutes}
            onChange={(event) => set.setCooldownMinutes(event.target.value)}
          />
        </div>
      ) : (
        <Alert tone="info" title={t("editRuleChannelTimersTitle")} announce={false}>
          {t("editRuleChannelTimersBody")}
        </Alert>
      )}

      <SelectField
        label={t("fieldSeverity")}
        value={state.severity}
        onChange={(value) => {
          if ((SEVERITIES as readonly string[]).includes(value)) {
            set.setSeverity(value as Severity);
          }
        }}
        data={SEVERITIES.map((value) => ({
          value,
          label: SEVERITY[value].label,
        }))}
      />

      <div className="flex items-start gap-2">
        <input
          id={enabledId}
          type="checkbox"
          className="mt-1 size-4 rounded border-border-subtle"
          checked={state.enabled}
          onChange={(event) => set.setEnabled(event.target.checked)}
        />
        <label htmlFor={enabledId} className="text-sm text-ink">
          <span className="font-medium">{t("fieldRuleEnabled")}</span>
          <span className="mt-0.5 block text-xs text-ink-muted">{t("fieldRuleEnabledHint")}</span>
        </label>
      </div>

      <ChannelToggle
        id={emailId}
        label={t("channelEmail")}
        hint={t("channelEmailHint")}
        checked={state.emailOn}
        onChange={set.setEmailOn}
      />

      {state.emailOn ? (
        <TextAreaField
          label={t("fieldEmailAddresses")}
          hint={t("fieldEmailAddressesHint")}
          rows={4}
          value={state.recipientsText}
          onChange={(event) => set.setRecipientsText(event.target.value)}
          {...(state.invalid.length > 0
            ? { error: t("invalidEmail", { list: state.invalid.join(", ") }) }
            : state.tooMany
              ? { error: t("tooManyRecipients", { max: MAX_RECIPIENTS }) }
              : state.recipients.length === 0
                ? { error: t("channelEmailRequired") }
                : {})}
        />
      ) : null}

      {state.emailOn && state.recipients.length > 0 && state.invalid.length === 0 ? (
        <p className="text-sm text-ink-muted">
          {state.recipients.length === 1
            ? t("recipientsCountOne")
            : t("recipientsCountMany", { count: state.recipients.length })}
        </p>
      ) : null}

      {state.emailOn ? (
        <Alert tone="info" title={t("dialogRecipientsEmailTitle")} announce={false}>
          {t("dialogRecipientsEmailBody")}
        </Alert>
      ) : null}

      <ChannelToggle
        id={whatsappId}
        label={t("channelWhatsapp")}
        hint={t("channelWhatsappHint")}
        checked={state.whatsappOn}
        onChange={set.setWhatsappOn}
      />

      {state.whatsappOn ? (
        <ContactChoices
          legend={t("selectWhatsappContacts")}
          empty={t("noWhatsappContacts")}
          contacts={whatsappChoices}
          selected={state.whatsappIds}
          onToggle={set.toggleWhatsapp}
          pending={contacts.isPending}
          {...(state.whatsappIds.length === 0 ? { error: t("channelWhatsappRequired") } : {})}
        />
      ) : null}

      <ChannelToggle
        id={voiceId}
        label={t("channelVoice")}
        hint={t("channelVoiceHint")}
        checked={state.voiceOn}
        onChange={set.setVoiceOn}
      />

      {state.voiceOn ? (
        <>
          <ContactChoices
            legend={t("selectVoiceContacts")}
            empty={t("noVoiceContacts")}
            contacts={voiceChoices}
            selected={state.voiceIds}
            onToggle={set.toggleVoice}
            pending={contacts.isPending}
            {...(state.voiceIds.length === 0 ? { error: t("channelVoiceRequired") } : {})}
          />
          <TextField
            label={t("fieldVoiceWait")}
            hint={t("fieldVoiceWaitHint", {
              min: VOICE_WAIT_MIN_MINUTES,
              max: VOICE_WAIT_MAX_MINUTES,
            })}
            type="number"
            min={VOICE_WAIT_MIN_MINUTES}
            max={VOICE_WAIT_MAX_MINUTES}
            step={1}
            required
            value={state.voiceWait}
            onChange={(event) => set.setVoiceWait(event.target.value)}
            {...(state.waitInvalid ? { error: t("fieldVoiceWaitInvalid") } : {})}
          />
        </>
      ) : null}

      {hears ? null : (
        <Alert tone="warn" title={t("dialogRecipientsNobodyTitle")}>
          {t("dialogRecipientsNobodyBody")}
        </Alert>
      )}
    </>
  );
}

interface EditAlertRuleDialogProps {
  rule: AlertRule;
  onClose: () => void;
}

/**
 * Tune a rule: bounds, timers, severity, enabled flag, and who hears.
 * Empty min/max means "defer to the asset" (ADR-048).
 */
export function EditAlertRuleDialog({ rule, onClose }: EditAlertRuleDialogProps) {
  const { t } = useTranslation("pages");
  const client = useApiClient();
  const queryClient = useQueryClient();
  const fields = useRuleFields(rule.metric, rule);
  const [formError, setFormError] = useState<string | null>(null);

  const mutation = useMutation({
    ...updateAlertRuleMutation(client, rule.id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["alert-rules"] });
    },
  });

  async function submit() {
    setFormError(null);
    const result = fields.collect();

    if (result === null) {
      return;
    }

    if ("error" in result) {
      setFormError(t(result.error, result.params));
      return;
    }

    const body: UpdateAlertRuleRequest = result.body;
    await mutation.mutateAsync(body);
    onClose();
  }

  const target = rule.assetName ?? rule.devEui ?? t("recipientsEveryDevice");

  return (
    <FormDialog
      open
      title={t("dialogEditRuleTitle")}
      description={`${ALERT_METRIC_LABELS[rule.metric]} · ${target}`}
      submitLabel={t("saveRule")}
      pending={mutation.isPending}
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
      onCancel={onClose}
    >
      <div className="flex flex-col gap-4">
        {mutation.isError ? <Alert>{mutation.error.message}</Alert> : null}
        {formError ? <Alert>{formError}</Alert> : null}

        <RuleFieldset
          metric={rule.metric}
          fields={fields}
          storedBounds={{ minValue: rule.minValue, maxValue: rule.maxValue }}
          organizationId={rule.organizationId}
        />
      </div>
    </FormDialog>
  );
}

interface CreateAlertRuleDialogProps {
  /** The organization the page is filtered to, for a super admin. */
  organizationId?: string;
  onClose: () => void;
}

const FIRST_METRIC: AlertMetric = ALERT_METRICS[0];

/**
 * A new rule for the whole organization or for one device (ADR-092). The
 * fields start from the metric's defaults and follow it when it changes.
 * There is one rule per metric and scope; the API answers a duplicate with a
 * conflict, which is shown as is.
 */
export function CreateAlertRuleDialog({ organizationId, onClose }: CreateAlertRuleDialogProps) {
  const { t } = useTranslation("pages");
  const { t: tCommon } = useTranslation("common");
  const client = useApiClient();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "SUPER_ADMIN";

  const [metric, setMetric] = useState<AlertMetric>(FIRST_METRIC);
  const [tenant, setTenant] = useState(organizationId ?? "");
  const [deviceId, setDeviceId] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const fields = useRuleFields(metric, {
    ...DEFAULT_ALERT_RULES[FIRST_METRIC],
    enabled: true,
    recipients: [],
    whatsappContactIds: [],
    voiceContactIds: [],
    voiceWaitMinutes: null,
  });

  const tenantChosen = !isSuperAdmin || tenant !== "";
  const organizations = useQuery({ ...organizationOptionsQuery(client), enabled: isSuperAdmin });
  const devices = useQuery({
    ...ruleDeviceOptionsQuery(client, isSuperAdmin ? tenant : undefined),
    enabled: tenantChosen,
  });

  const mutation = useMutation({
    ...createAlertRuleMutation(client),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["alert-rules"] });
    },
  });

  async function submit() {
    setFormError(null);

    if (!tenantChosen) {
      setFormError(t("createRuleOrganizationRequired"));
      return;
    }

    const result = fields.collect();

    if (result === null) {
      return;
    }

    if ("error" in result) {
      setFormError(t(result.error, result.params));
      return;
    }

    const body: CreateAlertRuleRequest = {
      metric,
      ...result.body,
      ...(deviceId ? { deviceId } : {}),
      ...(isSuperAdmin ? { organizationId: tenant } : {}),
    };

    await mutation.mutateAsync(body);
    onClose();
  }

  const defaults = DEFAULT_ALERT_RULES[metric];

  return (
    <FormDialog
      open
      title={t("dialogCreateRuleTitle")}
      description={t("dialogCreateRuleDescription")}
      submitLabel={t("createRule")}
      pending={mutation.isPending}
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
      onCancel={onClose}
    >
      <div className="flex flex-col gap-4">
        {mutation.isError ? <Alert>{mutation.error.message}</Alert> : null}
        {formError ? <Alert>{formError}</Alert> : null}

        {isSuperAdmin ? (
          <SelectField
            label={tCommon("organization")}
            required
            value={tenant}
            onChange={(value) => {
              setTenant(value);
              setDeviceId("");
            }}
            data={(organizations.data?.data ?? []).map((organization) => ({
              value: organization.id,
              label: organization.name,
            }))}
          />
        ) : null}

        <SelectField
          label={tCommon("metric")}
          required
          value={metric}
          onChange={(value) => {
            const next = ALERT_METRICS.find((candidate) => candidate === value);

            if (next) {
              setMetric(next);
              fields.reset(DEFAULT_ALERT_RULES[next]);
            }
          }}
          data={ALERT_METRICS.map((value) => ({ value, label: ALERT_METRIC_LABELS[value] }))}
        />

        <SelectField
          label={t("fieldRuleScope")}
          hint={t("fieldRuleScopeHint")}
          value={deviceId}
          disabled={!tenantChosen}
          onChange={setDeviceId}
          data={[
            { value: "", label: t("appliesEveryDevice") },
            ...(devices.data?.data ?? []).map((device) => ({
              value: device.id,
              label: device.assetName ? `${device.assetName} · ${device.devEui}` : device.devEui,
            })),
          ]}
        />

        <RuleFieldset
          metric={metric}
          fields={fields}
          storedBounds={{ minValue: defaults.minValue, maxValue: defaults.maxValue }}
          {...(isSuperAdmin && tenant !== "" ? { organizationId: tenant } : {})}
        />
      </div>
    </FormDialog>
  );
}
